/**
 * Per-CNPJ-per-section attachments uploader for the AEP report.
 *
 * Lets the user upload PDF / Excel / Word / Image files to a specific AEP
 * section. Images are rendered inline in the printable area (so they end up
 * in the PDF). PDF / Excel / Word files are listed as "anexos digitais" with
 * filename + download link.
 *
 * Backend:
 *   POST   /api/cnpj/{cnpj}/aep-attachments/{section_key}  (multipart)
 *   GET    /api/cnpj/{cnpj}/aep-attachments                 (grouped by sk)
 *   GET    /api/cnpj/{cnpj}/aep-attachments/{id}/download
 *   DELETE /api/cnpj/{cnpj}/aep-attachments/{id}
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import PdfThumbnail from "@/pages/aep/PdfThumbnail";

const ICON_FOR_MIME = (mime = "") => {
  if (mime.startsWith("image/")) return "fa-image";
  if (mime.includes("pdf")) return "fa-file-pdf";
  if (mime.includes("sheet") || mime.includes("excel")) return "fa-file-excel";
  if (mime.includes("word")) return "fa-file-word";
  return "fa-file";
};

const fmtSize = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const downloadHref = (cnpj, id) => {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}/api/cnpj/${cnpj}/aep-attachments/${id}/download`;
};

export default function SectionAttachments({
  cnpj, sectionKey, label,
  attachments, onChange, accept = ".pdf,.xls,.xlsx,.doc,.docx,.png,.jpg,.jpeg,.webp",
}) {
  const items = attachments?.[sectionKey] || [];
  const [uploading, setUploading] = useState(false);

  if (!cnpj) return null;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-uploading same file
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Arquivo excede 8 MB.");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post(
        `/cnpj/${cnpj}/aep-attachments/${sectionKey}`, form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      onChange?.(sectionKey, "add", r.data);
      toast.success(`Arquivo "${file.name}" anexado.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar arquivo.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (att) => {
    if (!window.confirm(`Remover anexo "${att.filename}"?`)) return;
    try {
      await api.delete(`/cnpj/${cnpj}/aep-attachments/${att.id}`);
      onChange?.(sectionKey, "delete", att);
      toast.success("Anexo removido.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao remover.");
    }
  };

  return (
    <div className="mt-3 border border-sky-200 bg-sky-50/30 rounded-md print:bg-transparent print:border-sky-200" data-testid={`aep-att-${sectionKey}`}>
      {/* Toolbar — hidden in print */}
      <div className="no-print flex flex-wrap items-center gap-2 px-3 py-2 border-b border-sky-200 text-xs">
        <span className="font-bold uppercase tracking-wider text-sky-900">
          <i className="fa-solid fa-paperclip mr-1"></i>
          Anexos desta seção {items.length > 0 && <span className="text-sky-600">({items.length})</span>}
        </span>
        <div className="flex-1"></div>
        <label className="inline-flex items-center gap-1 rounded bg-sky-700 text-white font-semibold px-3 py-1.5 cursor-pointer hover:bg-sky-800">
          {uploading
            ? <><i className="fa-solid fa-spinner fa-spin"></i> Enviando…</>
            : <><i className="fa-solid fa-upload"></i> Anexar arquivo</>}
          <input
            type="file"
            className="hidden"
            accept={accept}
            disabled={uploading}
            onChange={upload}
            data-testid={`aep-att-input-${sectionKey}`}
          />
        </label>
      </div>

      {/* Empty state — hidden in print */}
      {items.length === 0 && (
        <div className="no-print text-xs text-slate-500 px-3 py-2 italic">
          Nenhum anexo. Envie o arquivo oficial do {label} (PDF, Excel, Word ou imagem — máximo 8MB).
        </div>
      )}

      {/* Files list — visible in BOTH screen and print */}
      {items.length > 0 && (
        <ul className="divide-y divide-sky-100 print:divide-slate-200">
          {items.map((att) => {
            const isImg = att.mime?.startsWith("image/");
            const isPdf = att.mime?.includes("pdf");
            return (
              <li
                key={att.id}
                className="px-3 py-2 avoid-break"
                data-testid={`aep-att-item-${att.id}`}
              >
                {/* Image preview (renders in PDF too) */}
                {isImg && att.data_url && (
                  <img
                    src={att.data_url}
                    alt={att.filename}
                    className="max-w-full border border-slate-200 rounded mb-2"
                    style={{ maxHeight: "350px", objectFit: "contain" }}
                  />
                )}
                {/* PDF: render first-page thumbnail inline (printable) */}
                {isPdf && (
                  <div className="mb-2">
                    <PdfThumbnail cnpj={cnpj} attId={att.id} filename={att.filename} width={520} />
                  </div>
                )}
                <div className="flex items-start gap-3">
                  {!isImg && !isPdf && (
                    <i className={`fa-solid ${ICON_FOR_MIME(att.mime)} text-3xl text-sky-700 shrink-0`}></i>
                  )}
                  <div className="flex-1 text-xs min-w-0">
                    <div className="font-bold text-slate-900 truncate">
                      <i className={`fa-solid ${ICON_FOR_MIME(att.mime)} mr-1.5 text-sky-700`}></i>
                      {att.filename}
                    </div>
                    <div className="text-slate-500 mt-0.5">
                      {att.mime} · {fmtSize(att.size_bytes)} · enviado em{" "}
                      {new Date(att.uploaded_at).toLocaleString("pt-BR")}
                    </div>
                    <a
                      href={downloadHref(cnpj, att.id)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sky-700 font-semibold hover:underline mt-1 inline-block no-print"
                      data-testid={`aep-att-download-${att.id}`}
                    >
                      <i className="fa-solid fa-download mr-1"></i>
                      Baixar arquivo original
                    </a>
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(att)}
                    className="no-print inline-flex items-center gap-1 rounded border border-slate-300 text-slate-500 font-bold px-2 py-1 hover:bg-rose-50 hover:text-rose-700 text-xs"
                    data-testid={`aep-att-remove-${att.id}`}
                    title="Remover"
                  >
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
